import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Ship, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatError } from "@/context/AuthContext";
import { toast } from "sonner";


export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      const m = formatError(err);
      setError(m); toast.error(m);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] text-slate-100 flex">
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img src="https://images.pexels.com/photos/1624694/pexels-photo-1624694.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
             alt="Cargo port" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B0F19] via-[#0B0F19]/70 to-transparent" />
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="relative z-10 flex flex-col justify-end p-12 w-full">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-md bg-gradient-to-br from-sky-500 to-blue-700 flex items-center justify-center">
              <Ship size={22} />
            </div>
            <div className="font-display font-bold text-3xl tracking-wide">LEAD ASIA CARGO</div>
          </div>
          <h1 className="font-display text-5xl font-bold leading-tight mb-3">
            Customs Brokerage<br/>Operations Portal
          </h1>
          <p className="text-slate-300 max-w-md">
            Pre-Alert → Documentation → Customs → Logistics → Processing → Release → Delivery → Billing.
            One pipeline, nine role scopes, zero chaos.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-md bg-gradient-to-br from-sky-500 to-blue-700 flex items-center justify-center">
              <Ship size={18} />
            </div>
            <div className="font-display font-bold text-2xl">LEAD ASIA CARGO</div>
          </div>
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.3em] text-sky-400 mb-2">Sign in</div>
            <h2 className="font-display text-4xl font-bold">Operations Portal</h2>
            <p className="text-slate-400 mt-2 text-sm">Use your assigned credentials to continue.</p>
          </div>
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input id="email" data-testid="input-login-email" type="email" required value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     className="mt-1.5 bg-slate-900/60 border-slate-700 text-slate-100 placeholder:text-slate-500"
                     placeholder="you@leadasia.com" autoComplete="email" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input id="password" data-testid="input-login-password" type="password" required value={password}
                     onChange={(e) => setPassword(e.target.value)}
                     className="mt-1.5 bg-slate-900/60 border-slate-700 text-slate-100 placeholder:text-slate-500"
                     placeholder="••••••••" autoComplete="current-password" />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2">{error}</div>
            )}
            <Button data-testid="button-login-submit" disabled={loading} type="submit"
                    className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white font-semibold">
              {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Signing in…</> : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
